import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './DocumentEditor.css';
import {
  Box,
  Paper,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import { useDocumentContext } from '../../context/DocumentContext';
import { markdownToHtml, htmlToMarkdown } from '../../utils/markdownConverter';
import {
  exportHtmlToPdfViaWord,
  PdfConversionError,
  checkLibreOfficeAvailability,
  checkTemplateAvailability,
} from '../../utils/pdfConversionService';
import {
  generateWordWithTemplate,
  prepareTemplateData,
  validateTemplateData,
  TemplateExportError,
} from '../../utils/exportWithTemplate';

// Register font family and size with Quill
const Font = Quill.import('formats/font');
Font.whitelist = [
  'sans-serif',
  'serif',
  'monospace',
  'roboto',
  'arial',
  'times-new-roman',
  'courier-new',
];
Quill.register(Font, true);

const Size = Quill.import('attributors/style/size');
Size.whitelist = ['small', 'normal', 'large', 'huge'];
Quill.register(Size, true);

// Enhanced List handling
const List = Quill.import('formats/list');
class EnhancedList extends List {
  static create(value: string) {
    const node = super.create(value);
    return node;
  }
}
Quill.register('formats/list', EnhancedList, true);

// Global interface declaration
declare global {
  interface Window {
    getLatestDocumentContent?: () => string;
    getLatestTOC?: () => string;
  }
}

const DocumentEditor: React.FC = () => {
  const { updateTitle, title } = useDocumentContext();

  // Component state - SIMPLIFIED
  const [editorContent, setEditorContent] = useState<string>('');
  const [isUpdatingFromExternal, setIsUpdatingFromExternal] = useState(false);

  // Export state
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isLibreOfficeAvailable, setIsLibreOfficeAvailable] =
    useState<boolean>(false);
  const [templateInfo, setTemplateInfo] = useState<any>(null);

  // Refs
  const quillRef = useRef<ReactQuill>(null);

  // Initialize LibreOffice and template/title page availability check
  useEffect(() => {
    const checkAvailability = async () => {
      try {
        const [libreOfficeAvailable, templateAvailability] = await Promise.all([
          checkLibreOfficeAvailability(),
          checkTemplateAvailability(),
        ]);

        setIsLibreOfficeAvailable(libreOfficeAvailable);
        setTemplateInfo(templateAvailability);

        // Log title page support status
        if (templateAvailability?.features?.titlePageSupport) {
          console.log('✅ Title page support is available');
        } else {
          console.log(
            '⚠️ Title page support not available - PDFs will export without title page'
          );
        }
      } catch (error) {
        console.warn('Failed to check server availability:', error);
        setIsLibreOfficeAvailable(false);
        setTemplateInfo(null);
      }
    };

    checkAvailability();
  }, []);

  // Expose global function to get current document content for saving
  useEffect(() => {
    window.getLatestDocumentContent = () => {
      if (editorContent) {
        try {
          return htmlToMarkdown(editorContent);
        } catch (error) {
          console.error('Error converting to markdown:', error);
          return editorContent; // Fallback to HTML
        }
      }
      return '';
    };

    return () => {
      delete window.getLatestDocumentContent;
    };
  }, [editorContent]);

  // Handle external document updates (from AI/ChatSection)
  useEffect(() => {
    const handleDocumentUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { markdown, title: newTitle } = customEvent.detail;

      console.log('📄 External document update received:', {
        markdownLength: markdown?.length,
        title: newTitle,
      });

      if (newTitle && newTitle !== title) {
        updateTitle(newTitle);
      }

      if (markdown) {
        setIsUpdatingFromExternal(true);
        try {
          const htmlContent = markdownToHtml(markdown);
          setEditorContent(htmlContent);
        } catch (error) {
          console.error('Error converting markdown to HTML:', error);
          setEditorContent(markdown); // Fallback to raw markdown
        } finally {
          // Reset flag after a short delay to allow ReactQuill to settle
          setTimeout(() => setIsUpdatingFromExternal(false), 100);
        }
      }
    };

    const handleDraftUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { markdown, title: newTitle } = customEvent.detail;

      console.log('📄 Draft document update received:', {
        markdownLength: markdown?.length,
        title: newTitle,
      });

      if (newTitle && newTitle !== title) {
        updateTitle(newTitle);
      }

      if (markdown) {
        setIsUpdatingFromExternal(true);
        try {
          const htmlContent = markdownToHtml(markdown);
          setEditorContent(htmlContent);
        } catch (error) {
          console.error('Error converting markdown to HTML:', error);
          setEditorContent(markdown); // Fallback to raw markdown
        } finally {
          setTimeout(() => setIsUpdatingFromExternal(false), 100);
        }
      }
    };

    window.addEventListener('document_update', handleDocumentUpdate);
    window.addEventListener('document_update_draft', handleDraftUpdate);

    return () => {
      window.removeEventListener('document_update', handleDocumentUpdate);
      window.removeEventListener('document_update_draft', handleDraftUpdate);
    };
  }, [title, updateTitle]);

  // Memoized modules configuration
  const modules = React.useMemo(
    () => ({
      toolbar: [
        [{ font: Font.whitelist }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link', 'image'],
        ['blockquote', 'code-block'],
        ['clean'],
      ],
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  // Simple, clean editor change handler - NO PROCESSING, NO DEBOUNCING
  const handleEditorChange = useCallback(
    (value: string) => {
      // Skip updates when content is being set from external sources
      if (isUpdatingFromExternal) {
        return;
      }

      console.log('📝 Editor content changed by user');
      setEditorContent(value);

      // Notify about editor content change
      window.dispatchEvent(
        new CustomEvent('editor_content_changed', {
          detail: {
            content: value,
            source: 'user',
            timestamp: Date.now(),
          },
        })
      );
    },
    [isUpdatingFromExternal]
  );

  // Export menu handlers
  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  // Export functions
  const handleExportPdfViaLibreOffice = async () => {
    if (!editorContent || !title) return;

    try {
      setIsExporting(true);

      let tocContent = '';
      if (window.getLatestTOC) {
        tocContent = window.getLatestTOC();
      }

      const properties = {
        title,
        subtitle: 'Konzeption',
        author: '',
      };

      const styleOptions = {
        fontFamily: 'Montserrat, Arial, sans-serif',
        fontSize: '12pt',
        // headerColor: '#ee8a81',
        // accentColor: '#666666',
      };

      await exportHtmlToPdfViaWord(
        title,
        editorContent,
        tocContent,
        properties,
        styleOptions
      );
      console.log('✅ PDF export completed successfully');
    } catch (error) {
      console.error('❌ PDF export failed:', error);
      if (error instanceof PdfConversionError) {
        alert(
          `PDF Export Failed: ${error.message}${error.details ? '\n\nDetails: ' + error.details : ''}`
        );
      } else {
        alert(
          'PDF export failed. Please try again or use the standard PDF export option.'
        );
      }
    } finally {
      setIsExporting(false);
      handleExportMenuClose();
    }
  };

  const handleExportWordWithTemplate = async () => {
    if (!editorContent || !title) return;

    try {
      setIsExporting(true);

      // Get markdown content
      const markdownContent = window.getLatestDocumentContent?.() || '';
      if (!markdownContent) {
        throw new Error('Failed to get markdown content');
      }

      // Get TOC content if available
      let tocContent = '';
      if (window.getLatestTOC) {
        tocContent = window.getLatestTOC();
      }

      // Prepare template data with TOC and markdown content
      const templateData = prepareTemplateData(markdownContent, tocContent, {
        title: title,
        subtitle: 'Konzeption',
        author: 'KI-Agent',
        clientName: '', // Can be filled via a dialog in the future
      });

      // Validate required fields
      validateTemplateData(templateData, ['title']);

      // Generate and download the document
      await generateWordWithTemplate(templateData, {
        filename: `${title
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, '-')
          .toLowerCase()}-template.docx`,
      });

      console.log('✅ Template-based Word export completed successfully');
    } catch (error) {
      console.error('❌ Template-based Word export failed:', error);
      if (error instanceof TemplateExportError) {
        alert(
          `Template Export Failed: ${error.message}${error.details ? '\n\nDetails: ' + error.details : ''}`
        );
      } else {
        alert(
          'Template export failed. Please try again or use the standard Word export option.'
        );
      }
    } finally {
      setIsExporting(false);
      handleExportMenuClose();
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2,
          borderBottom: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          bgcolor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Document Title"
            value={title}
            onChange={(e) => updateTitle(e.target.value)}
          />

          <Tooltip title="Export document">
            <IconButton
              color="primary"
              onClick={handleExportMenuOpen}
              disabled={isExporting || !title}
              size="small"
              className="export-button"
              sx={{
                'backgroundColor': 'primary.main',
                'color': 'white',
                'borderRadius': '4px',
                'padding': '8px',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              {isExporting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {isLibreOfficeAvailable && (
              <MenuItem
                onClick={handleExportPdfViaLibreOffice}
                disabled={isExporting}
              >
                <PictureAsPdfIcon
                  fontSize="small"
                  sx={{ mr: 1, color: '#CC6659' }}
                />
                Export as PDF
              </MenuItem>
            )}

            <MenuItem
              onClick={handleExportWordWithTemplate}
              disabled={isExporting}
            >
              <ArticleIcon
                fontSize="small"
                sx={{ mr: 1, color: 'primary.main' }}
              />
              Export as Word
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Paper
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: 'none',
          border: 'none',
        }}
      >
        <Box
          sx={{ flex: 1, overflow: 'auto' }}
          className="document-editor-container"
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={editorContent}
            onChange={handleEditorChange}
            modules={modules}
            placeholder="Start writing your document..."
            style={{ height: '100%' }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default DocumentEditor;
